





















































import { computed, defineComponent, onMounted, toRefs, watch } from "@vue/composition-api";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import useImageAttachment from "@/hooks/useImageAttachment";
import useVideoAttachment from "@/hooks/useVideoAttachment";
import { isIOS, isMobile } from "@/lib/browserUtils";
import { isAudioFile, isDocumentFile, isTextFile } from "@/lib/fileHelpers";
import AudioPlayer from "@/components/AudioPlayer";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import DocumentAttachmentPreview from "@/components/DocumentAttachmentPreview";
import PlayButton from "@/components/PlayButton";
import MediaItemStatus from "./components/MediaItemStatus.vue";

export default defineComponent({
  name: "MediaGalleryItem",
  components: { DocumentAttachmentPreview, AudioPlayer, PlayButton, MediaItemStatus, BaseConfirm, BaseIcon },
  props: {
    attachment: { type: Object as () => MessageAttachment | FileData, required: true },
    isAuthor: { type: Boolean, required: true },
    isFullScreen: { type: Boolean, required: true },
    isActive: { type: Boolean, required: true },
    videoAutoPlay: { type: Boolean, default: false },
    videoStartTime: { type: Number, default: 0 },
    showBackground: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const file = computed(() => ("file" in props.attachment ? props.attachment.file : props.attachment));
    const isDocument = computed(() => isTextFile(file.value) || isDocumentFile(file.value));
    const isAudio = isAudioFile(file.value);
    const audioFileName = computed(() => {
      return file.value.metadata.filename.replace(/\.[^/.]+$/, "");
    });
    const { attachment, isFullScreen, isActive, videoAutoPlay, videoStartTime } = toRefs(props);
    const isProcessed = computed(() => file.value.processingStatus === "OK");

    const {
      isVideo,
      videoSrc,
      videoThumbnail,
      shouldPlayVideo,
      playVideo,
      pauseVideo,
      videoStarted,
      videoPlaying,
      videoElement,
      setVideoStartTime,
    } = useVideoAttachment(attachment, isActive, isFullScreen, isProcessed, videoStartTime);
    const { isImage, imageThumbnail } = useImageAttachment(attachment, isFullScreen);

    const attachmentThumbnail = computed(() => {
      if (isImage.value) return imageThumbnail.value;
      if (isVideo.value) return videoThumbnail.value;
      return "";
    });

    const handleClickOnVideo = () => {
      if (isIOS() && isMobile()) return; //on IOS used touch events

      if (shouldPlayVideo.value) {
        playVideo();
      } else {
        pauseVideo();
      }
    };

    const click = () => {
      if (isVideo.value) return handleClickOnVideo();
      emit("click");
    };

    onMounted(() => {
      if (isActive.value && isVideo.value && videoAutoPlay.value) {
        playVideo();
      }
    });

    watch(videoElement, () => {
      if (videoElement.value) {
        setVideoStartTime(videoStartTime.value);
        videoElement.value.ontimeupdate = () => {
          emit("currentTimeChange", videoElement.value?.currentTime);
        };
      }
    });

    watch(videoStarted, () => {
      emit("videoStarted", videoStarted.value);
    });
    watch(videoPlaying, () => {
      emit("videoPlaying", videoPlaying.value);
    });

    return {
      file,
      isProcessed,
      attachmentThumbnail,
      isAudio,
      isImage,
      isVideo,
      videoSrc,
      videoStarted,
      click,
      handleClickOnVideo,
      playVideo,
      videoElement,
      audioFileName,
      isDocument,
    };
  },
});
